import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { LegacyFrameContext } from "@/contexts/LegacyFrameContext";
import { useClassNames } from "@/hooks/useClassNames";
import { LayoutContext } from "@/contexts/LayoutContext";
import { QuickTaskContext } from "@/contexts/QuickTaskContext";
import { useLayoutData } from "@/hooks/useLayoutData";
import { useSideNavState } from "@/hooks/useSideNavState";
import { useIsMobile } from "@/hooks/use-mobile";
import { useWindowOpenerCallbacks } from "@/hooks/useWindowOpenerCallbacks";
import { LoadingPage } from "@/components/common/Loading/LoadingPage";
import { TopBar } from "./TopBar";
import { SideNav } from "./side-nav";
import { SettingsPanel } from "./side-nav/SettingsPanel";
import { NavPanelContext, SETTINGS_PANEL_ID, useNavPanelState } from "./side-nav/NavPanelContext";
import { QuickTaskPanel, useQuickTaskPanel } from "./quicktask";
import { MegaFooter } from "./MegaFooter";
import { ErrorPage } from "./ErrorPage";
import { WhatsNewProvider } from "./whats-new/WhatsNewContext";
import { AiChatPanel } from "@/components/specific/ai/AiChatPanel";
import { isAskVectaPath } from "@/components/specific/ai/chatNavigation";
import { fetchMenuItems } from "@/api/menu/api";
import { getCustomerContextId } from "@/utils/customerContext";
import type { MenuItem } from "@/types/domain";

export type AppShellProps = {
  children: ReactNode;
}

export const AppShell = ({ children }: AppShellProps) => {
  const { data, loading, error } = useLayoutData();
  const { state: sideNavState, toggle: toggleSideNav } = useSideNavState();
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const toggleMobileNav = () => setMobileNavOpen((v) => !v);
  const location = useLocation();
  const isFullPageChat = isAskVectaPath(location.pathname);
  const [legacyActive, setLegacyActive] = useState(false);
  const { panelState, hasEverOpened, prefill, openPanel, openNewTask, minimisePanel, closePanel } = useQuickTaskPanel();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState(false);
  const menuCache = useRef(new Map<string, MenuItem[]>());
  const navPanel = useNavPanelState();
  const settingsOpen = navPanel.activeId === SETTINGS_PANEL_ID;
  const setSettingsOpen = (next: boolean) => (next ? navPanel.open(SETTINGS_PANEL_ID) : navPanel.close(SETTINGS_PANEL_ID));
  const [settingsAnchorLeft, setSettingsAnchorLeft] = useState(0);
  useWindowOpenerCallbacks(data?.appRoutes);

  const openSettings = () => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    const navRight = document.querySelector(".layout-side-nav")?.getBoundingClientRect().right ?? 0;
    setSettingsAnchorLeft(isDesktop ? navRight : 0);
    navPanel.toggle(SETTINGS_PANEL_ID);
  };
  const availableMenuItems = isMobile ? menuItems.filter((i) => i.type !== "settings") : menuItems;
  const settingsItem = availableMenuItems.find((i) => i.type === "settings" && i.isVisible);
  const returnFocusToSettingsButton = (event: Event) => {
    event.preventDefault();
    document.querySelector<HTMLElement>('[data-testid="maintenance-submenu-button"]')?.focus();
  };

  useEffect(() => {
    if (isMobile && settingsOpen) navPanel.close(SETTINGS_PANEL_ID);
  }, [isMobile, settingsOpen, navPanel]);

  useEffect(() => {
    const url = window.location.href;
    const cached = menuCache.current.get(url);
    if (cached) {
      setMenuItems(cached);
      setMenuLoading(false);
      return;
    }
    let cancelled = false;
    fetchMenuItems(url)
      .then((items) => {
        if (cancelled) return;
        menuCache.current.set(url, items);
        setMenuItems(items);
        setMenuError(false);
        setMenuLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setMenuError(true);
        setMenuLoading(false);
      });
    return () => { cancelled = true; };
  }, [location.pathname, location.search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingPage message="Loading..." />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorPage />;
  }

  return (
    <LayoutContext.Provider value={{ data, sideNavState, toggleSideNav, mobileNavOpen, toggleMobileNav }}>
      <NavPanelContext.Provider value={navPanel}>
      <QuickTaskContext.Provider value={{ openQuickTask: () => openNewTask(getCustomerContextId()) }}>
        <div
          className={useClassNames("layout-root font-sans", sideNavState === "mini" && "mini", mobileNavOpen && "nav-open")}
        >
          <WhatsNewProvider>
          <TopBar onOpenSettings={settingsItem ? openSettings : undefined} settingsSelected={!!settingsItem?.isSelected} />
          <SideNav menuItems={availableMenuItems} menuLoading={menuLoading} menuError={menuError} onOpenSettings={settingsItem ? openSettings : undefined} />
          {settingsItem && (
            <SettingsPanel settings={settingsItem} open={settingsOpen} onOpenChange={setSettingsOpen} anchorLeft={settingsAnchorLeft} onCloseAutoFocus={returnFocusToSettingsButton} />
          )}
          <main className="layout-page-content">
            <LegacyFrameContext.Provider value={setLegacyActive}>
              <div className={isFullPageChat || legacyActive ? "flex flex-1 flex-col bg-white" : "flex-1 bg-white"}>
                {children}
              </div>
            </LegacyFrameContext.Provider>
            {!isFullPageChat && !legacyActive && <MegaFooter routes={data.appRoutes} />}
          </main>
          <QuickTaskPanel panelState={panelState} hasEverOpened={hasEverOpened} prefill={prefill} onOpen={openPanel} onMinimise={minimisePanel} onClose={closePanel} />
          {data.isAiChatAvailable && !isFullPageChat && <AiChatPanel showToggle={false} />}
          </WhatsNewProvider>
        </div>
      </QuickTaskContext.Provider>
      </NavPanelContext.Provider>
    </LayoutContext.Provider>
  );
}
